import { inject, Injectable } from '@angular/core';
import { switchMap, tap } from 'rxjs/operators';

import { DataSource, GqlService } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { Utils } from '@shared/utils/utils';

import { createInitialState, IntegrationsStore } from './integrations.store';

@Injectable({ providedIn: 'root' })
export class IntegrationsService {
  private readonly gqlService = inject(GqlService);
  private readonly integrationsStore = inject(IntegrationsStore);
  private readonly mainQuery = inject(MainQuery);
  private readonly overlayService = inject(OverlayService);

  mapLogo = new Map<DataSource, string>([
    [DataSource.DATA_SOURCE_AUXILIUS, 'assets/img/logo.png'],
    [DataSource.DATA_SOURCE_BILL_COM, 'assets/img/billcom.png'],
    [DataSource.DATA_SOURCE_COUPA, 'assets/img/coupa.png'],
    [DataSource.DATA_SOURCE_DYNAMICS365, 'assets/img/microsoft-dynamics.png'],
    [DataSource.DATA_SOURCE_DYNAMICS365_FO, 'assets/img/microsoft-dynamics.png'],
    [DataSource.DATA_SOURCE_FLAT_FILE, 'assets/img/logo-dark-2.png'],
    [DataSource.DATA_SOURCE_NETSUITE, 'assets/img/netsuite.png'],
    [DataSource.DATA_SOURCE_ORACLE_FUSION, 'assets/img/oracle-fusion.png'],
    [DataSource.DATA_SOURCE_QUICKBOOKS_ONLINE, 'assets/img/quickbooks.png'],
    [DataSource.DATA_SOURCE_SAGE_INTACCT, 'assets/img/sage-intacct.png'],
  ]);
  mapType = new Map<DataSource, string>([
    [DataSource.DATA_SOURCE_AUXILIUS, 'Auxilius'],
    [DataSource.DATA_SOURCE_BILL_COM, 'Invoices'],
    [DataSource.DATA_SOURCE_COUPA, 'Invoices'],
    [DataSource.DATA_SOURCE_DYNAMICS365, 'Invoices'],
    [DataSource.DATA_SOURCE_DYNAMICS365_FO, 'Invoices'],
    [DataSource.DATA_SOURCE_FLAT_FILE, 'Invoices'],
    [DataSource.DATA_SOURCE_NETSUITE, 'Invoices'],
    [DataSource.DATA_SOURCE_ORACLE_FUSION, 'Invoices'],
    [DataSource.DATA_SOURCE_QUICKBOOKS_ONLINE, 'Invoices'],
    [DataSource.DATA_SOURCE_SAGE_INTACCT, 'Invoices'],
  ]);

  getIntegrations() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.integrationsStore.setLoading(true);
        this.integrationsStore.update(createInitialState());

        return this.gqlService.listIntegrationConnections$().pipe(
          tap(({ data, errors, success }) => {
            if (success && data) {
              const intArray = data
                .filter((x) => x.enabled)
                .sort((x, y) => Utils.alphaNumSort(x.name, y.name))
                .map((x) => {
                  return {
                    ...x,
                    logo: this.mapLogo.get(x.data_source) || '',
                    type: this.mapType.get(x.data_source) || '',
                  };
                });

              this.integrationsStore.update({
                items: intArray || [],
              });
            } else {
              this.overlayService.error(errors);
            }

            this.integrationsStore.setLoading(false);
          })
        );
      })
    );
  }
}
